export type ScenarioId = string & { readonly __brand: 'ScenarioId' };
export type NodeId = string & { readonly __brand: 'NodeId' };
export type ManagerId = string & { readonly __brand: 'ManagerId' };
export type EdgeId = string & { readonly __brand: 'EdgeId' };

export const createScenarioId = (id: string): ScenarioId => id as ScenarioId;
export const createNodeId = (id: string): NodeId => id as NodeId;
export const createManagerId = (id: string): ManagerId => id as ManagerId;
export const createEdgeId = (id: string): EdgeId => id as EdgeId;

export interface NodeAddition<TProps = Record<string, unknown>> {
  id: NodeId;
  type: string;
  name: string;
  title?: string;
  department?: string;
  managerId?: ManagerId | null;
  position?: { readonly x: number; readonly y: number };
  props?: TProps;
}

export interface NodeRemoval<TProps = Record<string, unknown>> {
  nodeId: NodeId;
  originalData: {
    name: string;
    title?: string;
    managerId?: ManagerId | null;
    type: string;
    props?: TProps;
  };
}

export interface NodeMove {
  nodeId: NodeId;
  oldManagerId: ManagerId | null;
  newManagerId: ManagerId | null;
}

export interface EdgeAddition<TProps = Record<string, unknown>> {
  id: EdgeId;
  type: string;
  from: NodeId;
  to: NodeId;
  props?: TProps;
}

export interface EdgeRemoval {
  edgeId: EdgeId;
  /** Node removal that implicitly owns this incident-edge removal, when any. */
  cascadeFromNodeId?: NodeId;
}

export type NodeUpdateChange<TField extends string = string, TValue = unknown> = {
  field: TField;
  oldValue: TValue;
  newValue: TValue;
};

export interface BindingChange {
  sourceId: string;
  recordId: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface NodeUpdate<TChange = NodeUpdateChange> {
  nodeId: NodeId;
  change: TChange;
  bindingChange?: BindingChange;
}

export interface ScenarioDelta<
  TProps = Record<string, unknown>,
  TChange = NodeUpdateChange
> {
  additions: Array<NodeAddition<TProps>>;
  removals: Array<NodeRemoval<TProps>>;
  moves: Array<NodeMove>;
  updates: Array<NodeUpdate<TChange>>;
  edgeAdditions: Array<EdgeAddition>;
  edgeRemovals: Array<EdgeRemoval>;
}

/** The portion of a scenario delta owned by one independently editable aggregate. */
export interface ScenarioAggregateOwnership {
  nodeIds?: NodeId[];
  nodeFields?: Record<string, string[]>;
  edgeIds?: EdgeId[];
  edgeTargets?: Array<{
    targetId: NodeId;
    relationTypes: string[];
  }>;
}

/** One independently convergent contribution to a scenario. */
export interface ScenarioAggregateContribution<
  TProps = Record<string, unknown>,
  TChange = NodeUpdateChange
> {
  aggregateKey: string;
  delta: ScenarioDelta<TProps, TChange>;
  ownership: ScenarioAggregateOwnership;
  updatedAt: string;
  updatedBy: string;
}

export const createEmptyDelta = <
  TProps = Record<string, unknown>,
  TChange = NodeUpdateChange
>(): ScenarioDelta<TProps, TChange> => ({
  additions: [],
  removals: [],
  moves: [],
  updates: [],
  edgeAdditions: [],
  edgeRemovals: [],
});

export const isDeltaEmpty = (delta: ScenarioDelta): boolean =>
  delta.additions.length === 0 &&
  delta.removals.length === 0 &&
  delta.moves.length === 0 &&
  delta.updates.length === 0 &&
  delta.edgeAdditions.length === 0 &&
  delta.edgeRemovals.length === 0;

export const countDeltaChanges = (delta: ScenarioDelta): number =>
  delta.additions.length +
  delta.removals.length +
  delta.moves.length +
  delta.updates.length +
  delta.edgeAdditions.length +
  delta.edgeRemovals.length;

export type DeltaOperation<TProps = Record<string, unknown>, TChange = NodeUpdateChange> =
  | { type: 'addition'; data: NodeAddition<TProps> }
  | { type: 'removal'; data: NodeRemoval<TProps> }
  | { type: 'move'; data: NodeMove }
  | { type: 'update'; data: NodeUpdate<TChange> }
  | { type: 'edge-addition'; data: EdgeAddition }
  | { type: 'edge-removal'; data: EdgeRemoval };

export const deltaToOperations = <
  TProps = Record<string, unknown>,
  TChange = NodeUpdateChange
>(delta: ScenarioDelta<TProps, TChange>): Array<DeltaOperation<TProps, TChange>> => [
  ...delta.additions.map((data) => ({ type: 'addition' as const, data })),
  ...delta.removals.map((data) => ({ type: 'removal' as const, data })),
  ...delta.moves.map((data) => ({ type: 'move' as const, data })),
  ...delta.updates.map((data) => ({ type: 'update' as const, data })),
  ...delta.edgeAdditions.map((data) => ({ type: 'edge-addition' as const, data })),
  ...delta.edgeRemovals.map((data) => ({ type: 'edge-removal' as const, data })),
];
